import {
	CurrencyEnum,
	OrderPaymentMethod,
	OrderSampleChoice,
	useCreateSamplePaymentMutation,
	useGetSamplePaymentPreviewQuery,
} from '@codezo/graphql';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { CheckCircle2, CreditCard, FileText, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '../ui/dialog';
import { cn } from '../../lib/utils';
import { PaymentTotals, money } from './PaymentTotals';

/* ── Types ── */

interface OrderItem {
	id: string;
	productName?: string | null;
	productImages?: Array<{ url?: string | null }> | null;
	quantity: number;
	sampleChoice?: string | null;
	samples?: Array<{
		id: string;
		status: string;
		totalPrice?: number | null;
		sampleChoice: string;
	}> | null;
}

interface PaymentMethodOption {
	method: OrderPaymentMethod;
	available: boolean;
	reason?: string | null;
}

interface PayForSamplesModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	orderId: string;
	items: OrderItem[];
	/**
	 * The samples to charge. Omit to charge every one currently payable — the
	 * order-level "Pay for samples" route, and the original behaviour.
	 *
	 * Passed when a single flow row opens this: that row is priced for one round,
	 * so the checkout has to charge that round and nothing else. Both the preview
	 * and the mutation take the same ids, so the figure quoted is the figure
	 * charged; an id that is not payable returns the empty preview rather than a
	 * trimmed one, and the mutation refuses the whole call rather than charging
	 * for part of a selection.
	 */
	sampleIds?: string[];
	paymentMethods?: PaymentMethodOption[] | null;
	/** PO captured at acceptance — pre-fills the checkout PO field (still editable). */
	orderPoNumber?: string | null;
	onSuccess?: () => void;
}

type Step = 'summary' | 'checkout' | 'complete';

const STRIPE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '';
let stripePromise: ReturnType<typeof loadStripe> | null = null;
function getStripe() {
	if (!stripePromise && STRIPE_KEY) stripePromise = loadStripe(STRIPE_KEY);
	return stripePromise;
}

/* ════════════════════════ MODAL ════════════════════════ */

export function PayForSamplesModal({
	open,
	onOpenChange,
	orderId,
	items,
	sampleIds,
	paymentMethods,
	orderPoNumber,
	onSuccess,
}: PayForSamplesModalProps) {
	const stripeOption = useMemo(
		() => paymentMethods?.find((m) => m.method === OrderPaymentMethod.Stripe),
		[paymentMethods]
	);
	const invoiceOption = useMemo(
		() => paymentMethods?.find((m) => m.method === OrderPaymentMethod.Invoice),
		[paymentMethods]
	);

	const defaultMethod = stripeOption?.available
		? OrderPaymentMethod.Stripe
		: invoiceOption?.available
			? OrderPaymentMethod.Invoice
			: OrderPaymentMethod.Stripe;
	const [paymentMethod, setPaymentMethod] = useState<OrderPaymentMethod>(defaultMethod);
	const [createPayment, { loading }] = useCreateSamplePaymentMutation();
	const [step, setStep] = useState<Step>('summary');
	const [clientSecret, setClientSecret] = useState<string | null>(null);
	const [poNumber, setPoNumber] = useState(orderPoNumber ?? '');

	// Pre-fill the PO from the order each time the modal opens (the client can
	// still edit it — whatever they leave here wins for this invoice).
	useEffect(() => {
		if (open) setPoNumber(orderPoNumber ?? '');
	}, [open, orderPoNumber]);

	// Narrowed to the selection when there is one, so the list names what is
	// actually being charged rather than every sample on the order.
	const sampledItems = items.filter(
		(i) =>
			i.samples &&
			i.samples.length > 0 &&
			i.sampleChoice &&
			i.sampleChoice !== OrderSampleChoice.NoSample &&
			(!sampleIds || i.samples.some((s) => sampleIds.includes(s.id)))
	);

	// Payment currency is fixed by method: Stripe always charges EUR, invoice always DKK.
	// The preview reflects the currency the customer will actually be billed in, not the
	// global currency selector.
	const previewCurrency =
		paymentMethod === OrderPaymentMethod.Stripe ? CurrencyEnum.Eur : CurrencyEnum.Dkk;

	// Fetch preview with real pricing. Keep the previous result visible while a
	// method switch refetches in the new currency — otherwise the whole breakdown
	// collapses to a spinner and re-expands on every toggle (jarring flicker).
	const {
		data: previewData,
		previousData: prevPreviewData,
		loading: previewLoading,
	} = useGetSamplePaymentPreviewQuery({
		variables: { orderId, currency: previewCurrency, sampleIds },
		fetchPolicy: 'no-cache',
		skip: !open,
	});
	const preview =
		previewData?.getSamplePaymentPreview ?? prevPreviewData?.getSamplePaymentPreview;

	const reset = () => {
		setStep('summary');
		setClientSecret(null);
		setPaymentMethod(defaultMethod);
		setPoNumber(orderPoNumber ?? '');
	};

	const handleClose = (next: boolean) => {
		if (!next) {
			if (step === 'complete') onSuccess?.();
			reset();
		}
		onOpenChange(next);
	};

	const handleContinue = async () => {
		try {
			const { data } = await createPayment({
				variables: {
					input: {
						orderId,
						paymentMethod,
						poNumber: poNumber.trim() || undefined,
						returnUrl: window.location.href,
						// Omitted for the all-samples route; the server then charges
						// everything currently payable.
						sampleIds,
					},
				},
			});

			const result = data?.createSamplePayment;
			if (!result?.success) {
				toast.error(result?.message || 'Failed to create payment');
				return;
			}

			if (result.clientSecret) {
				setClientSecret(result.clientSecret);
				setStep('checkout');
			} else {
				toast.success(result.message || 'Invoice booked. You will receive it by email.');
				handleClose(false);
				onSuccess?.();
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to create payment');
		}
	};

	const handleCheckoutComplete = () => {
		setStep('complete');
		onSuccess?.();
	};

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent
				className="sm:max-w-lg"
				showCloseButton={step !== 'checkout'}
				{...(step === 'checkout'
					? {
							onEscapeKeyDown: (e: Event) => e.preventDefault(),
							onPointerDownOutside: (e: Event) => e.preventDefault(),
						}
					: {})}
			>
				<DialogHeader>
					<DialogTitle>
						{step === 'summary' && 'Pay for samples'}
						{step === 'checkout' && 'Checkout'}
						{step === 'complete' && 'Payment confirmed'}
					</DialogTitle>
				</DialogHeader>

				<DialogBody className="scroll-thin max-h-[70vh] overflow-y-auto p-0">
					{/* ── Summary ── */}
					{step === 'summary' && (
						<div className="flex flex-col">
							{/* Payment method picker */}
							<div className="border-b border-line-1 px-4 py-3">
								<div className="mb-2 text-xs font-medium uppercase tracking-[0.06em] text-text-4">
									Payment method
								</div>
								<div className="flex gap-1.5">
									<button
										type="button"
										disabled={stripeOption?.available === false}
										onClick={() => setPaymentMethod(OrderPaymentMethod.Stripe)}
										className={cn(
											'flex flex-1 items-center justify-center gap-2 rounded-lg border py-2.5 text-sm font-medium transition-colors',
											paymentMethod === OrderPaymentMethod.Stripe
												? 'border-accent bg-accent-soft text-accent'
												: 'border-line-2 text-text-3 hover:border-line-3',
											stripeOption?.available === false &&
												'cursor-not-allowed opacity-40'
										)}
									>
										<CreditCard size={14} />
										Card (EUR)
									</button>
									<button
										type="button"
										disabled={invoiceOption?.available === false}
										onClick={() => setPaymentMethod(OrderPaymentMethod.Invoice)}
										className={cn(
											'flex flex-1 items-center justify-center gap-2 rounded-lg border py-2.5 text-sm font-medium transition-colors',
											paymentMethod === OrderPaymentMethod.Invoice
												? 'border-accent bg-accent-soft text-accent'
												: 'border-line-2 text-text-3 hover:border-line-3',
											invoiceOption?.available === false &&
												'cursor-not-allowed opacity-40'
										)}
									>
										<FileText size={14} />
										Invoice (DKK)
									</button>
								</div>
								{stripeOption?.available === false && stripeOption.reason && (
									<p className="mt-2 text-xs text-status-warn">
										Card: {stripeOption.reason}
									</p>
								)}
								{invoiceOption?.available === false && invoiceOption.reason && (
									<p className="mt-2 text-xs text-status-warn">
										Invoice: {invoiceOption.reason}
									</p>
								)}
								<p className="mt-2 text-xs text-text-4">
									{paymentMethod === OrderPaymentMethod.Stripe
										? 'Pay securely via Stripe. Amount will be charged in EUR.'
										: 'An e-conomic invoice will be generated and sent to the invoice recipient.'}
								</p>
							</div>

							{/* PO number */}
							<div className="border-b border-line-1 px-4 py-3">
								<label
									htmlFor="sample-po-number"
									className="mb-2 block text-xs font-medium uppercase tracking-[0.06em] text-text-4"
								>
									PO number{' '}
									<span className="text-text-4 normal-case">(optional)</span>
								</label>
								<Input
									id="sample-po-number"
									value={poNumber}
									onChange={(e) => setPoNumber(e.target.value)}
									placeholder="e.g. PO-12345"
								/>
							</div>

							{/* Sample items with preview */}
							{!preview && previewLoading ? (
								<div className="flex items-center justify-center py-10">
									<Loader2 size={20} className="animate-spin text-text-3" />
								</div>
							) : preview ? (
								<div
									className={cn(
										'transition-opacity',
										previewLoading && 'opacity-50'
									)}
								>
									<div className="border-b border-line-1">
										<div className="px-4 pt-3 pb-1 text-xs font-medium uppercase tracking-[0.06em] text-text-4">
											Samples ({preview.items.length})
										</div>
										{preview.items.map((line, i) => (
											<div
												key={line.sampleId}
												className="px-4 py-2.5"
												style={{
													borderBottom:
														i < preview.items.length - 1
															? '0.5px solid var(--line-1)'
															: 'none',
												}}
											>
												<div className="flex items-center justify-between">
													<span className="text-sm font-medium text-text-1">
														{line.description}
													</span>
													<span className="text-sm font-medium text-text-1">
														{preview.currency}{' '}
														{money(
															line.samplePrice + line.shippingPrice
														)}
													</span>
												</div>
												{/* Freight is charged on top of the sample, so the
												    line has to break down or the rows won't sum to
												    the subtotal below. Zero when it isn't shipped. */}
												{line.shippingPrice > 0 && (
													<div className="mt-1 flex flex-col gap-0.5 text-xs">
														<div className="flex items-center justify-between">
															<span className="text-text-4">
																Sample
															</span>
															<span className="text-text-3">
																{preview.currency}{' '}
																{money(line.samplePrice)}
															</span>
														</div>
														<div className="flex items-center justify-between">
															<span className="text-text-4">
																Shipping
															</span>
															<span className="text-text-3">
																{preview.currency}{' '}
																{money(line.shippingPrice)}
															</span>
														</div>
													</div>
												)}
											</div>
										))}
									</div>

									{/* Net / VAT / gross — the total is what will be charged */}
									<PaymentTotals
										currency={preview.currency}
										grandTotal={preview.grandTotal}
										vatRate={preview.vatRate}
										vatAmount={preview.vatAmount}
										grossAmount={preview.grossAmount}
									/>
								</div>
							) : (
								<div className="px-4 py-8 text-center text-sm text-text-4">
									Unable to load pricing preview.
								</div>
							)}
						</div>
					)}

					{/* ── Stripe checkout ── */}
					{step === 'checkout' && (
						<div className="min-h-[420px] px-4 pb-4">
							{clientSecret && getStripe() ? (
								<EmbeddedCheckoutProvider
									key={clientSecret}
									stripe={getStripe()!}
									options={{ clientSecret, onComplete: handleCheckoutComplete }}
								>
									<EmbeddedCheckout />
								</EmbeddedCheckoutProvider>
							) : (
								<div className="flex min-h-[360px] flex-col items-center justify-center gap-3">
									<Loader2 size={24} className="animate-spin text-text-3" />
									<p className="text-base text-text-3">
										Preparing secure checkout…
									</p>
								</div>
							)}
						</div>
					)}

					{/* ── Complete ── */}
					{step === 'complete' && (
						<div className="flex flex-col items-center gap-4 py-12 text-center">
							<div className="grid h-12 w-12 place-items-center rounded-full bg-status-success/10">
								<CheckCircle2 size={24} className="text-status-success" />
							</div>
							<div>
								<div className="text-lg font-medium text-text-1">
									Payment confirmed
								</div>
								<p className="mt-1 text-base text-text-3">
									Your sample payment has been processed successfully.
								</p>
							</div>
						</div>
					)}
				</DialogBody>

				{step === 'summary' && (
					<DialogFooter>
						<Button variant="outline" onClick={() => handleClose(false)}>
							Cancel
						</Button>
						<Button
							onClick={handleContinue}
							disabled={
								loading ||
								sampledItems.length === 0 ||
								(paymentMethod === OrderPaymentMethod.Stripe &&
									stripeOption?.available === false) ||
								(paymentMethod === OrderPaymentMethod.Invoice &&
									invoiceOption?.available === false)
							}
						>
							{loading
								? 'Processing…'
								: paymentMethod === OrderPaymentMethod.Stripe
									? 'Continue to payment'
									: 'Continue to invoice'}
						</Button>
					</DialogFooter>
				)}

				{step === 'complete' && (
					<DialogFooter>
						<Button onClick={() => handleClose(false)}>Done</Button>
					</DialogFooter>
				)}
			</DialogContent>
		</Dialog>
	);
}
